import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { FocusTrap } from '@mui/base';
import { styled, Button as MUIButton, ClickAwayListener, Fade, Popper } from '@mui/material';

import { useViewport } from 'contexts/viewport';
import Box from 'shell/components/Box';
import Button from 'shell/components/Button';
import Checkbox from 'shell/components/Checkbox';
import Divider from 'shell/components/Divider';
import IconButton from 'shell/components/IconButton';
import MenuItem from 'shell/components/MenuItem';
import Paper from 'shell/components/Paper';
import Select from 'shell/components/Select';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import TextInput from 'shell/components/TextInput';

const CustomBox = styled(Box)(() => {
    return {
        width: '100%',
    };
});

const CustomTextInput = styled(TextInput)(({ disabled, theme }) => {
    return {
        '& > .MuiInputBase-root': {
            ...(disabled && {
                background: theme.palette.ensightful.neutral[300],
            }),
        },
        '& > .MuiInputBase-root, & input': {
            cursor: disabled ? 'default' : 'pointer',
        },
        '& > div > input::placeholder': {
            opacity: 1,
            color: theme.palette.ensightful.neutral[900],
        },
    };
});

const CustomIconButton = styled(IconButton)(({ disabled }) => {
    return {
        ...(disabled && {
            cursor: 'default',
        }),
    };
});

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        borderRadius: '12px',
        padding: theme.spacing(5, 5, 2),
        boxShadow: theme.palette.ensightful.shadows[1],
    };
});

const CustomStack = styled(Stack)(() => {
    return {
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    };
});

const CalendarRoot = styled(Box)(() => {
    return {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, auto)',
        width: 280,
    };
});

const CalendarHeader = styled(Stack)(() => {
    return {
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
    };
});

const CalendarDate = styled(MUIButton)(({ $disabled, isSelected, theme }) => {
    return {
        border: `0px solid ${theme.palette.ensightful.primary[500]}`,
        borderRadius: '50%',
        minWidth: 'unset',
        width: 40,
        height: 40,
        color: theme.palette.ensightful.neutral[900],
        '&:hover': {
            border: `1px solid ${theme.palette.ensightful.primary[500]}`,
            background: theme.palette.ensightful.shades[0],
        },
        '&.Mui-focusVisible': {
            border: `1px solid ${theme.palette.ensightful.primary[500]}`,
            background: theme.palette.ensightful.shades[0],
        },
        ...($disabled && {
            border: 'none',
            color: theme.palette.ensightful.neutral[400],
            cursor: 'default',
            pointerEvents: 'none',
            '&.Mui-focusVisible': {
                border: `1px solid ${theme.palette.ensightful.neutral[400]}`,
            },
        }),
        ...(isSelected && {
            background: theme.palette.ensightful.primary[500],
            color: theme.palette.ensightful.shades[0],
            '&:hover': {
                background: theme.palette.ensightful.primary[500],
            },
            '&.Mui-focusVisible': {
                background: theme.palette.ensightful.primary[700],
            },
        }),
    };
});

const getCalendarMonth = (month, year) => {
    const daysInMonth = moment().year(year).month(month).daysInMonth();
    const currentMonth = Array.from(Array(daysInMonth)).map((_, index) => {
        return moment()
            .year(year)
            .month(month)
            .date(index + 1)
            .hour(0)
            .minute(0)
            .second(0)
            .millisecond(0);
    });
    const currentMonthFirstDay = currentMonth[0];
    const currentMonthLastDay = currentMonth[currentMonth.length - 1];
    const partialPrevMonth = Array.from(Array(currentMonthFirstDay.day())).map((_, index) => {
        return moment(currentMonthFirstDay).subtract(currentMonthFirstDay.day() - index, 'days');
    });
    const partialNextMonth = Array.from(Array(6 - currentMonthLastDay.day())).map((_, index) => {
        return moment(currentMonthLastDay).add(index + 1, 'days');
    });
    return [...partialPrevMonth, ...currentMonth, ...partialNextMonth];
};

const CalendarGrid = ({ disabledDate, month, year, value, onChange }) => {
    const calendarMonth = useMemo(() => getCalendarMonth(month, year), [month, year]);

    return (
        <CalendarRoot>
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                <CalendarHeader key={day}>
                    <Text variant={'p2'}>{day}</Text>
                </CalendarHeader>
            ))}
            {calendarMonth.map((date, index) => {
                const isDisabled = date.month() !== month || disabledDate(date);
                const isSelected = date.format('YYYY M D') === value;
                return (
                    <CalendarDate
                        key={index}
                        aria-disabled={isDisabled}
                        $disabled={isDisabled}
                        isSelected={isSelected}
                        variant={'text'}
                        onClick={() => onChange(date.format('YYYY M D'))}
                    >
                        <Text color={'inherit'} component={'span'} variant={'p2'}>
                            {date.format('D')}
                        </Text>
                    </CalendarDate>
                );
            })}
        </CalendarRoot>
    );
};

export default ({
    id,
    allowTimeSelection,
    disabled,
    disabledDate = () => false,
    error,
    forceTimeSelection = false,
    helperText,
    label,
    placeholder = 'Select',
    required,
    value,
    onChange,
    ...rest
}) => {
    const { device } = useViewport();

    const anchorRef = useRef(null);

    const [open, setOpen] = useState(false);
    const [ignoreClickAway, setIgnoreClickAway] = useState(false);

    const defaultValue = value || moment().hour(0).minute(0).second(0).millisecond(0);

    const [date, setDate] = useState(defaultValue.format('YYYY M D'));
    const [hour, setHour] = useState(defaultValue.format('hh'));
    const [minute, setMinute] = useState(defaultValue.format('mm'));
    const [ampm, setAMPM] = useState(defaultValue.format('a'));
    const [activeMonthYear, setActiveMonthYear] = useState({
        month: defaultValue.month(),
        year: defaultValue.year(),
    });
    const [isSelectingTime, setIsSelectingTime] = useState(forceTimeSelection);

    if (forceTimeSelection) allowTimeSelection = true;

    const formatValue = (value) => {
        if (!value) return '';
        return value.year() === moment().year()
            ? value.format(allowTimeSelection ? `MMM D [at] hh:mma` : `MMM D`)
            : value.format(allowTimeSelection ? `MMM D YYYY [at] hh:mma` : `MMM D YYYY`);
    };

    const handleClickAway = (e) => {
        if (ignoreClickAway) return;
        if (anchorRef.current && anchorRef.current.contains(e.target)) return;
        setOpen(false);
    };

    const displayPrevMonth = () => {
        setActiveMonthYear(({ month, year }) => {
            const newValue = moment().year(year).month(month).subtract(1, 'month');
            return { month: newValue.month(), year: newValue.year() };
        });
    };

    const displayNextMonth = () => {
        setActiveMonthYear(({ month, year }) => {
            const newValue = moment().year(year).month(month).add(1, 'month');
            return { month: newValue.month(), year: newValue.year() };
        });
    };

    const stateToMoment = (date, hour, minute, ampm) => {
        const str = moment(`${date} ${hour}:${minute} ${ampm}`, ['YYYY M D hh:mm a']).format();
        return allowTimeSelection ? moment(str) : moment(str).hour(0).minute(0).second(0).millisecond(0);
    };

    const handleClickCancel = () => {
        setOpen(false);
    };

    const handleClickOk = () => {
        onChange(stateToMoment(date, hour, minute, ampm));
        setOpen(false);
    };

    const mappedSize = {
        mobile: 'medium',
        tablet: 'medium',
        desktop: 'medium',
    }[device];

    useEffect(() => {
        if (!open && value) {
            setTimeout(() => {
                setDate(value.format('YYYY M D'));
                setHour(value.format('hh'));
                setMinute(value.format('mm'));
                setAMPM(value.format('a'));
                setActiveMonthYear({ month: value.month(), year: value.year() });
            }, 200);
        }
    }, [open, value]);

    useEffect(() => {
        if (!isSelectingTime) {
            setHour('12');
            setMinute('00');
            setAMPM('am');
        }
    }, [isSelectingTime]);

    const DatePickerInner = (
        <Stack divider={<Divider />} tabIndex={-1} spacing={3}>
            <CustomStack direction={'row'}>
                <Text variant={'h6'}>
                    {moment().month(activeMonthYear.month).format('MMMM')}{' '}
                    {moment().year(activeMonthYear.year).format('YYYY')}
                </Text>
                <Stack direction={'row'}>
                    <IconButton
                        aria-label={'previous month'}
                        kind={'chevronLeft'}
                        size={'small'}
                        variant={'square'}
                        onClick={displayPrevMonth}
                    />
                    <IconButton
                        aria-label={'next month'}
                        kind={'chevronRight'}
                        size={'small'}
                        variant={'square'}
                        onClick={displayNextMonth}
                    />
                </Stack>
            </CustomStack>
            <CalendarGrid
                disabledDate={disabledDate}
                month={activeMonthYear.month}
                year={activeMonthYear.year}
                value={date}
                onChange={setDate}
            />
            {allowTimeSelection && (
                <>
                    <Stack spacing={3}>
                        {!forceTimeSelection && (
                            <Checkbox
                                sx={{ my: -2 }}
                                checked={isSelectingTime}
                                label={'Select a Time'}
                                onChange={(e) => setIsSelectingTime(e.target.checked)}
                            />
                        )}
                        {isSelectingTime && (
                            <Stack direction={'row'} spacing={2}>
                                <Select
                                    data-cy={'hour-picker'}
                                    size={'small'}
                                    value={hour}
                                    onChange={(e) => setHour(e.target.value)}
                                    onClose={() => setIgnoreClickAway(false)}
                                    onOpen={() => setIgnoreClickAway(true)}
                                >
                                    {Array.from(Array(12)).map((_, index) => {
                                        const item = String(index + 1).padStart(2, '0');
                                        return (
                                            <MenuItem data-cy={`hour-picker-${item}`} key={index} value={item}>
                                                {item}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                                <Select
                                    size={'small'}
                                    value={minute}
                                    onChange={(e) => setMinute(e.target.value)}
                                    onClose={() => setIgnoreClickAway(false)}
                                    onOpen={() => setIgnoreClickAway(true)}
                                >
                                    <MenuItem value={'00'}>00</MenuItem>
                                    <MenuItem value={'30'}>30</MenuItem>
                                </Select>
                                <Select
                                    data-cy={'am-pm-picker'}
                                    size={'small'}
                                    value={ampm}
                                    onChange={(e) => setAMPM(e.target.value)}
                                    onClose={() => setIgnoreClickAway(false)}
                                    onOpen={() => setIgnoreClickAway(true)}
                                >
                                    <MenuItem data-cy={'am-picker'} value={'am'}>
                                        AM
                                    </MenuItem>
                                    <MenuItem data-cy={'pm-picker'} value={'pm'}>
                                        PM
                                    </MenuItem>
                                </Select>
                            </Stack>
                        )}
                    </Stack>
                </>
            )}
            <Stack direction={'row-reverse'}>
                <Button size={'small'} variant={'tertiary'} onClick={handleClickOk}>
                    Ok
                </Button>
                <Button color={'neutral'} size={'small'} variant={'tertiary'} onClick={handleClickCancel}>
                    Cancel
                </Button>
            </Stack>
        </Stack>
    );

    return (
        <CustomBox {...rest}>
            <CustomTextInput
                ref={anchorRef}
                disabled={disabled}
                disablePointerEventsOnIcon={false}
                endIcon={
                    <CustomIconButton
                        id={id}
                        aria-label={'choose date'}
                        disabled={disabled}
                        edge={'end'}
                        kind={'calendar'}
                        size={mappedSize}
                        variant={'square'}
                        onClick={() => setOpen((current) => !current)}
                    />
                }
                error={error}
                helperText={helperText}
                inputProps={{ readOnly: true }}
                label={label}
                placeholder={placeholder}
                required={required}
                value={formatValue(value)}
                onClick={disabled ? undefined : () => setOpen(true)}
            />
            <Popper
                sx={{
                    zIndex: (theme) => theme.zIndex.modal,
                }}
                anchorEl={anchorRef.current}
                open={open}
                placement={'auto'}
                transition
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps}>
                        <Box>
                            <ClickAwayListener onClickAway={handleClickAway}>
                                <CustomPaper>
                                    {open ? <FocusTrap open={open}>{DatePickerInner}</FocusTrap> : DatePickerInner}
                                </CustomPaper>
                            </ClickAwayListener>
                        </Box>
                    </Fade>
                )}
            </Popper>
        </CustomBox>
    );
};
